import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Role, Permission, roleService } from '../services/roleService';
import '../css/standard-components.css';

const ViewRole: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);

  useEffect(() => {
    if (id) {
      loadRole();
      loadPermissions();
    }
  }, [id]);

  const loadRole = async () => {
    try {
      setLoading(true);
      const roleData = await roleService.getRoleById(parseInt(id!));
      setRole(roleData);
      setRolePermissions(roleData.rolePermissions?.map(rp => rp.permission) || []);
    } catch (error) {
      console.error('Error loading role:', error);
      alert('Error loading role');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const permissionsData = await roleService.getPermissions();
      setPermissions(permissionsData.permissions);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="page-container">
        <div className="error">Role not found</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>View Role: {role.name}</h1>
        <div className="page-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/auth/roles')}
          >
            Back to Roles
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/auth/roles/edit?id=${role.id}`)}
          >
            Edit Role
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="standard-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Role Name</label>
              <div className="form-value">{role.name}</div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <div className="form-value">{role.description || 'No description'}</div>
            </div>

            <div className="form-group">
              <label>Status</label>
              <div className="form-value">
                <span className="status-badge status-active">
                  Active
                </span>
              </div>
            </div>

            <div className="form-group">
              <label>Created</label>
              <div className="form-value">{new Date(role.createdAt).toLocaleDateString()}</div>
            </div>

            <div className="form-group">
              <label>Last Updated</label>
              <div className="form-value">{new Date(role.updatedAt).toLocaleDateString()}</div>
            </div>
          </div>

          <div className="form-section">
            <h3>Assigned Permissions ({rolePermissions.length})</h3>
            {rolePermissions.length === 0 ? (
              <div className="no-data">No permissions assigned to this role</div>
            ) : (
              <div className="permissions-grid">
                {rolePermissions.map(permission => (
                  <div key={permission.id} className="permission-item">
                    <div className="permission-name">{permission.name}</div>
                    <div className="permission-description">{permission.description}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewRole;
